import { WasteItemUomInfo } from '../../../app/services/api/generated';
import { UomOption } from '../interfaces/ItemWasteChecksheetData';

/**
 * Build UomOption[] for an item from its conversions, always including the base UOM.
 */
export const getAvailableUoms = (
  baseUom: string,
  conversions?: WasteItemUomInfo[],
): UomOption[] => {
  const uoms: UomOption[] = [
    { code: baseUom, description: baseUom, conversionNumber: 1 },
  ];

  if (conversions) {
    for (const conv of conversions) {
      if (conv.code && conv.code !== baseUom) {
        uoms.push({
          code: conv.code,
          description: conv.description ?? conv.code,
          conversionNumber: conv.conversionNumber,
          fromUom: conv.fromUom,
        });
      }
    }
  }

  return uoms;
};

/**
 * Format conversion info for display, showing base UOM and all conversions.
 * e.g. "EA\n1 CS = 12 EA\n1 PK = 6 EA"
 */
export const formatConversions = (
  baseUom: string,
  conversions?: WasteItemUomInfo[],
): string => {
  if (!conversions || conversions.length === 0) return baseUom;

  const conversionLines = conversions
    .filter((c) => c.code && c.code !== baseUom && c.conversionNumber)
    .map((c) => `1 ${c.code} = ${c.conversionNumber} ${c.fromUom ?? baseUom}`);

  if (conversionLines.length === 0) return baseUom;

  return [baseUom, ...conversionLines].join('\n');
};

/**
 * Convert a quantity from one UOM to another using conversion factors.
 * Returns the converted quantity, or the original if no conversion is found.
 */
export const convertQuantity = (
  qty: number,
  fromUom: string,
  toUom: string,
  availableUoms: UomOption[],
): number => {
  if (fromUom === toUom || qty === 0) return qty;

  const fromOption = availableUoms.find((u) => u.code === fromUom);
  const toOption = availableUoms.find((u) => u.code === toUom);

  if (!fromOption?.conversionNumber || !toOption?.conversionNumber) return qty;

  // Convert: fromUom → base → toUom
  // fromOption.conversionNumber = how many base units in 1 fromUom
  // toOption.conversionNumber = how many base units in 1 toUom
  const baseQty = qty * fromOption.conversionNumber;
  return baseQty / toOption.conversionNumber;
};

/**
 * Calculate total cost for an item across all active reasons.
 * baseCostPrice is in the item's base UOM.
 */
export const calculateItemCost = (
  reasonQuantities: { qty: number; uom: string }[],
  baseCostPrice: number,
  baseUom: string,
  availableUoms: UomOption[],
): number => {
  let totalCost = 0;
  for (const { qty, uom } of reasonQuantities) {
    if (qty <= 0) continue;
    // Convert qty to base UOM, then multiply by base cost
    const baseQty = convertQuantity(qty, uom, baseUom, availableUoms);
    totalCost += baseQty * baseCostPrice;
  }
  return totalCost;
};
